import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PrintDisabledIcon from "@mui/icons-material/PrintDisabled";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useSelector } from "react-redux";
import { PATH_APP } from "src/routes/paths";

// ----------------------------------------------------------------------
// Clasificación de problemas — da pasos específicos según qué falla.
// ----------------------------------------------------------------------

function categorize(printer) {
  if (!printer.isConnected) {
    const msg = (printer.lastError || "").toLowerCase();
    if (msg.includes("timeout") || msg.includes("no responde") || msg.includes("offline")) {
      return "unreachable";
    }
    if (msg.includes("no instalada") || msg.includes("not installed")) {
      return "not-installed";
    }
    return "disconnected";
  }
  if (!printer.paperOk) return "paper";
  if (!printer.coverOk) return "cover";
  return "other";
}

const ACTIONS = {
  unreachable: {
    title: "No se puede conectar con la impresora",
    bullets: [
      "Si es de red: verifica que esté encendida y con su LED verde de WiFi/Ethernet activo.",
      "Comprueba que el cable de red (o WiFi) esté conectado al router del restaurante.",
      "Desde la pestaña Instalar → sección WiFi → usa 'ping' desde el PC del worker para verificar conectividad.",
      "Si la impresora cambió de IP, bórrala y descúbrela de nuevo desde 'Estado y descubrir'.",
    ],
  },
  "not-installed": {
    title: "Impresora no reconocida por Windows",
    bullets: [
      "Abre 'Dispositivos e impresoras' en Windows y verifica que aparece.",
      "Si no está, desconecta y reconecta el USB. Espera a que Windows instale el driver.",
      "Si el nombre en Windows no coincide exactamente con el que pusiste en el TPV, bórrala y créala con el nombre correcto.",
    ],
  },
  disconnected: {
    title: "Impresora desconectada",
    bullets: [
      "Verifica cable USB o Ethernet firmemente conectado.",
      "Apaga la impresora 10 segundos y vuelve a encenderla.",
      "Si es de red, comprueba que el router del restaurante esté funcionando.",
    ],
  },
  paper: {
    title: "Sin papel",
    bullets: [
      "Abre la tapa y repón el rollo de papel térmico.",
      "Asegúrate de que el rollo está correctamente orientado (la cara térmica va hacia fuera).",
      "Cierra la tapa hasta oír el click.",
      "La impresora debe avanzar un trocito de papel automáticamente.",
    ],
  },
  cover: {
    title: "Tapa abierta",
    bullets: [
      "Cierra la tapa de la impresora presionando hasta oír el click.",
      "Si la tapa no cierra bien, revisa que el rollo esté correctamente colocado (a veces un rollo mal puesto impide que cierre).",
    ],
  },
  other: {
    title: "Incidencia",
    bullets: [
      "Apaga la impresora 10 segundos y vuelve a encenderla.",
      "Si persiste, consulta la pestaña Instalar para la guía completa.",
    ],
  },
};

// ----------------------------------------------------------------------

export default function PrinterIssuesHelpDialog({ open, onClose }) {
  const history = useHistory();
  const { workers } = useSelector((s) => s.workerStatus);

  // Solo impresoras de workers online (consistente con el banner).
  const issues = useMemo(() => {
    if (!workers) return [];
    return workers
      .filter((w) => w.isOnline)
      .flatMap((w) =>
        (w.printers || [])
          .filter((p) => !p.isConnected || !p.paperOk || !p.coverOk || p.lastError)
          .map((p) => ({ ...p, _cat: categorize(p) })),
      );
  }, [workers]);

  const goToStatus = () => {
    history.push(PATH_APP.tuCarta.printerSystemStatus);
    onClose();
  };

  const goToInstall = () => {
    history.push(PATH_APP.tuCarta.printerInstall);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PrintDisabledIcon color="warning" />
          <Typography variant="h6">Impresora con incidencia</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Por qué importa:</strong> las comandas que pasen por una impresora en este
          estado se pondrán en cola en el worker y se reintentarán hasta 8 veces. Si el problema
          persiste varios minutos, el ticket se marcará como <strong>Perdido</strong>.
        </Alert>

        {issues.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay impresoras con incidencias en este momento. Si ves el banner, probablemente
            acabe de resolverse — espera unos segundos a que el heartbeat confirme.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {issues.map((p) => {
              const action = ACTIONS[p._cat] || ACTIONS.other;
              return (
                <Box key={p.idPrinter}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                    <Typography variant="subtitle2">{p.name}</Typography>
                    <Chip size="small" color="warning" label={action.title} />
                  </Stack>
                  {p.lastError && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Motivo reportado: <em>{p.lastError}</em>
                    </Typography>
                  )}
                  <List dense disablePadding>
                    {action.bullets.map((b, i) => (
                      <ListItem key={i} disableGutters alignItems="flex-start">
                        <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                          <CheckCircleOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={<Typography variant="body2">{b}</Typography>} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <HelpOutlineIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
          <Typography variant="caption" color="text.secondary">
            Si tras los pasos anteriores no se resuelve, revisa la guía completa en la pestaña
            Instalar (cubre WiFi y USB) o mira los errores recientes en Insights.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button variant="outlined" onClick={goToInstall}>Guía completa</Button>
        <Button variant="contained" onClick={goToStatus}>Ver estado</Button>
      </DialogActions>
    </Dialog>
  );
}
